import { useCallback, useEffect, useRef, useState } from 'react';
import { clearStartedAt, readStartedAt, writeStartedAt } from '../persistentTime';

export type RestPhase = 'idle' | 'resting' | 'ready' | 'over';

export interface RestTimer {
  phase: RestPhase;
  /**
   * How far through the current countdown segment we are, 0..1. Used for the
   * accessible value; the visual fill is animated by CSS from the two fields
   * below so it interpolates smoothly rather than stepping each sample.
   */
  progress: number;
  /** Length of the current countdown segment in seconds (0 when complete). */
  segmentTotal: number;
  /** Seconds already spent in the current segment. */
  segmentElapsed: number;
  /**
   * The number to show, always counting DOWN:
   *   resting - seconds until the rest window opens (low - elapsed)
   *   ready   - seconds left in the rest window (high - elapsed)
   *   over    - 0
   */
  countdown: number;
  /** Seconds past the end of the window, for the `over` phase. */
  overBy: number;
  /** Raw seconds since the set finished. */
  elapsed: number;
  /**
   * When the running rest began (epoch ms), null while idle. A new rest gets a
   * new value even if the phase happens to repeat, which is what lets the fill
   * animation know to restart from the left.
   */
  startedAt: number | null;
  start: () => void;
  stop: () => void;
}

/**
 * Rest timer for the gap between sets.
 *
 * Counts down twice rather than up: first to the moment the rest window opens,
 * then through the window itself.
 *
 *   |<-- countdown 1 -->|<-- countdown 2 -->|
 *   set ends          low end            high end
 *   (resting)          (ready)             (over)
 *
 * Anchored to a wall-clock timestamp instead of accumulated ticks, so the value
 * stays correct when a phone backgrounds the tab and throttles timers.
 */
/**
 * Pure phase/countdown math, split out so it can be tested without a timer.
 * `elapsed` is seconds since the set finished.
 */
export function restStateAt(
  elapsed: number,
  lowSecs: number | null | undefined,
  highSecs: number | null | undefined,
): {
  phase: Exclude<RestPhase, 'idle'>;
  countdown: number;
  overBy: number;
  progress: number;
  segmentTotal: number;
  segmentElapsed: number;
} {
  const low = Math.max(0, lowSecs ?? 0);
  // A malformed window (high < low) collapses to a point rather than inverting.
  const high = Math.max(highSecs ?? low, low);

  // A zero-length segment is already complete rather than dividing by zero.
  const frac = (done: number, total: number) =>
    total <= 0 ? 1 : Math.min(1, Math.max(0, done / total));

  if (elapsed < low) {
    return {
      phase: 'resting',
      countdown: low - elapsed,
      overBy: 0,
      progress: frac(elapsed, low),
      segmentTotal: low,
      segmentElapsed: elapsed,
    };
  }
  if (elapsed <= high) {
    return {
      phase: 'ready',
      countdown: high - elapsed,
      overBy: 0,
      progress: frac(elapsed - low, high - low),
      segmentTotal: high - low,
      segmentElapsed: elapsed - low,
    };
  }
  return {
    phase: 'over',
    countdown: 0,
    overBy: elapsed - high,
    progress: 1,
    segmentTotal: 0,
    segmentElapsed: 0,
  };
}

export function useRestTimer(
  lowSecs: number | null | undefined,
  highSecs: number | null | undefined,
  /**
   * When given, the running timer is persisted under this key and resumed on
   * mount — so a rest period survives navigating away, refreshing, or closing
   * the browser. Only the start timestamp is stored; elapsed is always derived.
   */
  storageKey?: string,
): RestTimer {
  const [startedAt, setStartedAt] = useState<number | null>(
    () => (storageKey ? readStartedAt(storageKey) : null),
  );
  const [now, setNow] = useState(() => Date.now());
  const startedAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (startedAt === null) return;
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [startedAt]);

  // Re-sync the moment the tab is foregrounded again.
  useEffect(() => {
    const onVisible = () => { if (!document.hidden) setNow(Date.now()); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  // Adopt a timer that was already running under this key (e.g. after a
  // refresh, or when the key changes because a different exercise opened).
  useEffect(() => {
    if (!storageKey) return;
    setStartedAt(readStartedAt(storageKey));
  }, [storageKey]);

  const start = useCallback(() => {
    const t = Date.now();
    startedAtRef.current = t;
    setStartedAt(t);
    setNow(t);
    if (storageKey) writeStartedAt(storageKey, t);
  }, [storageKey]);

  const stop = useCallback(() => {
    startedAtRef.current = null;
    setStartedAt(null);
    if (storageKey) clearStartedAt(storageKey);
  }, [storageKey]);

  if (startedAt === null) {
    return {
      phase: 'idle', countdown: 0, overBy: 0, progress: 0,
      segmentTotal: 0, segmentElapsed: 0, elapsed: 0, startedAt: null, start, stop,
    };
  }

  const elapsed = Math.max(0, Math.floor((now - startedAt) / 1000));
  return { ...restStateAt(elapsed, lowSecs, highSecs), elapsed, startedAt, start, stop };
}

/** mm:ss for a non-negative second count. */
export function formatSecs(total: number): string {
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
